import React, { memo } from 'react';
import { Phase } from '@/types/Document';
import StepRenderer from './StepRenderer';
import ItemActionMenu from './ItemActionMenu';

interface PhaseRendererProps {
  phase: Phase;
  flowIndex: number;
  phaseIndex: number;
  editMode: boolean;
  isCollapsed: boolean;
  openMenuPath: string | null;
  setOpenMenuPath: React.Dispatch<React.SetStateAction<string | null>>;
  togglePhaseCollapse: (flowIndex: number, phaseIndex: number) => void;
  handleInputChange: (
    path: (string | number)[],
    value: string,
    type?: 'flow' | 'phase' | 'step' | 'block'
  ) => void;
  handleDeleteItem: (itemPath: string) => void;
  handleAddItem: (parentPath: string, itemTypeToAdd: 'phase' | 'step' | 'block') => void;
  handleChangeItemType: (itemPath: string, newType: 'phase' | 'step') => void;
  handleCreateStepFromSelection: (selection: string, blockPath: string) => void;
}

const PhaseRenderer: React.FC<PhaseRendererProps> = memo(({
  phase,
  flowIndex,
  phaseIndex,
  editMode,
  isCollapsed,
  openMenuPath,
  setOpenMenuPath,
  togglePhaseCollapse,
  handleInputChange,
  handleDeleteItem,
  handleAddItem,
  handleChangeItemType,
  handleCreateStepFromSelection,
}) => {
  const phasePath = `phase-${flowIndex}-${phaseIndex}`;
  const isMenuOpen = openMenuPath === phasePath;
  const hasSteps = phase.steps && phase.steps.length > 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3">
      <div 
        className={`flex justify-between items-center ${!editMode ? 'cursor-pointer' : ''} ${hasSteps ? 'mb-2' : ''}`} 
        onClick={!editMode ? () => togglePhaseCollapse(flowIndex, phaseIndex) : undefined}
      >
        <div className="flex items-center flex-grow mr-4">
          {hasSteps && (
            <span className="mr-2 text-gray-500 flex-shrink-0" aria-hidden="true">
              {isCollapsed ? '▶' : '▼'} 
            </span>
          )}
          {editMode ? (
            <input
              type="text"
              value={phase.name}
              onChange={(e) => 
                handleInputChange(
                  [flowIndex, 'phases', phaseIndex, 'name'], 
                  e.target.value, 
                  'phase'
                )
              }
              className="flex-grow text-md font-semibold uppercase text-gray-700 w-full p-1 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          ) : (
            <h4 className="text-md font-semibold text-gray-700 uppercase">
              {phase.name}
            </h4>
          )}
        </div>
        <div className="flex space-x-1 text-gray-400 relative flex-shrink-0">
          {editMode && (
            <button 
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpenMenuPath(isMenuOpen ? null : phasePath);
              }}
              className="p-1 rounded hover:bg-gray-200"
              title="Phase Actions"
              aria-label="Phase actions menu"
            >
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                className="h-5 w-5" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor" 
                strokeWidth={2}
                aria-hidden="true"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z" 
                />
              </svg>
            </button>
          )}
          {isMenuOpen && (
            <ItemActionMenu 
              itemPath={phasePath}
              itemType="phase"
              onClose={() => setOpenMenuPath(null)}
              onAddItem={handleAddItem}
              onDeleteItem={handleDeleteItem}
              onChangeItemType={handleChangeItemType}
              onCreateStepFromSelection={handleCreateStepFromSelection}
            />
          )}
        </div>
      </div>
      
      {/* Steps Rendering */}
      {!isCollapsed && hasSteps && (
        <div className="mt-2 space-y-3 pl-4 border-l-2 border-gray-100">
          {phase.steps.map((step, stepIndex) => (
            <StepRenderer
              key={`step-${flowIndex}-${phaseIndex}-${stepIndex}`}
              step={step}
              flowIndex={flowIndex}
              phaseIndex={phaseIndex}
              stepIndex={stepIndex}
              editMode={editMode}
              openMenuPath={openMenuPath}
              setOpenMenuPath={setOpenMenuPath}
              handleInputChange={handleInputChange}
              handleDeleteItem={handleDeleteItem}
              handleAddItem={handleAddItem}
              handleChangeItemType={handleChangeItemType}
              handleCreateStepFromSelection={handleCreateStepFromSelection}
            />
          ))}
        </div>
      )}
    </div>
  );
});

PhaseRenderer.displayName = 'PhaseRenderer';

export default PhaseRenderer;
